import { entityDisplay } from './entityDisplay';

/**
 * entityNormalizer.ts
 *
 * Universal DTO types and normalizers for Patient, HCP, and Hospital entities.
 * Handles any raw API shape and provides consistent, null-safe display values.
 *
 * Contract: API → normalize*Dto() → UI
 * UI components must NEVER consume raw API shapes directly.
 *
 * Normalizers are safe to call with null / undefined — they always return
 * a fully-populated DTO with human-readable fallback strings.
 *
 * Usage:
 *   import { normalizePatientDto, safe } from '@/modules/shared/utils/entityNormalizer';
 *
 *   const patient = normalizePatientDto(rawApiResponse);
 *   <span>{safe(patient.patientCode)}</span>
 *   <span>{patient.hospitalName}</span>
 */

// ─── Fallback constants ───────────────────────────────────────────────────────

export const NOT_AVAILABLE = 'Not available';
export const NOT_LINKED    = 'Not linked';

/** Regex matching title-only strings like "Dr.", "Prof." that are not real names */
const TITLE_ONLY_RE = /^(dr\.?|prof\.?|mr\.?|mrs\.?|ms\.?|sr\.?|jr\.?)$/i;

// ─── safe() ──────────────────────────────────────────────────────────────────

/**
 * Returns `value` when it is a non-empty string, otherwise `fallback` (default "—").
 *
 * Designed for JSX: eliminates blank cells / empty strings silently.
 *
 * @example
 *   <span>{safe(patient.patientCode)}</span>
 *   <span>{safe(patient.hospitalName, 'Not assigned')}</span>
 */
export function safe(value: string | null | undefined, fallback = '—'): string {
  if (!value || value.trim() === '') return fallback;
  return value;
}

// ─── Standard DTO types ───────────────────────────────────────────────────────

/**
 * PatientDTO — Standard patient data contract consumed by ALL UI components.
 * Create only via normalizePatientDto(); never construct manually from raw API.
 */
export interface PatientDTO {
  /** GUID of the patient */
  id: string;
  /** Human-readable patient code. Never empty — falls back to a safe placeholder. */
  patientCode: string;
  /** Resolved full name. Never empty — falls back to NOT_AVAILABLE. */
  fullName: string;
  firstName?: string;
  lastName?: string;
  /** Resolved hospital display name. Never empty. */
  hospitalName: string;
  /** Resolved HCP display name (title-only names are rejected). Never empty. */
  hcpName: string;
  /** Status string in lowercase, e.g. "active", "inactive". Never empty. */
  status: string;
  /** ISO date of birth */
  dob?: string;
  age?: number;
  /** Total financial assistance received */
  totalAssistance: number;
  /** Remaining eligibility amount */
  remainingEligibility: number;
  createdAt?: string;
}

/**
 * HcpDTO — Standard HCP data contract.
 */
export interface HcpDTO {
  id: string;
  /** Resolved full name, title-only names rejected. */
  fullName: string;
  specialization?: string;
}

/**
 * HospitalDTO — Standard hospital data contract.
 */
export interface HospitalDTO {
  id: string;
  /** Hospital display name. */
  name: string;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function resolveId(r: Record<string, unknown>): string {
  return ((r.id ?? r.patientId ?? r.entityId ?? '') as string);
}

function resolveCode(r: Record<string, unknown>): string {
  const code = (r.patientCode ?? r.code ?? r.patient_id) as string | undefined;
  return entityDisplay.patient({ patientCode: code }).label;
}

function resolveFullName(r: Record<string, unknown>): string {
  // Direct fullName field
  if (r.fullName && typeof r.fullName === 'string' && r.fullName.trim()) {
    return r.fullName.trim();
  }

  // Resolve from formData (PatientDto from /patients endpoint)
  const formData = r.formData instanceof Object ? (r.formData as Record<string, unknown>) : {};
  const fromForm =
    (formData.patientName as string) ||
    (formData.patient_name as string) ||
    (formData.name as string) ||
    (formData.fullName as string) ||
    (formData.full_name as string) ||
    (`${formData.firstName || ''} ${formData.lastName || ''}`.trim()) ||
    '';
  if (fromForm) return fromForm;

  // Resolve from root-level first/last name
  const fromRoot = `${r.firstName || ''} ${r.lastName || ''}`.trim();
  if (fromRoot) return fromRoot;

  // patientName field (legacy)
  if (r.patientName && typeof r.patientName === 'string' && r.patientName.trim()) {
    return r.patientName.trim();
  }

  return NOT_AVAILABLE;
}

function resolveHospitalName(r: Record<string, unknown>): string {
  const hosp = r.hospital instanceof Object ? (r.hospital as Record<string, unknown>) : null;
  const name =
    (hosp?.name as string) ||
    (r.hospitalName as string) ||
    '';
  return name.trim() || NOT_AVAILABLE;
}

function resolveHcpName(r: Record<string, unknown>): string {
  const hcp = r.hcp instanceof Object ? (r.hcp as Record<string, unknown>) : null;
  const candidates: string[] = [
    (hcp?.fullName as string) || '',
    hcp ? `${hcp.firstName || ''} ${hcp.lastName || ''}`.trim() : '',
    (r.hcpName as string) || '',
    (r.hcpFullName as string) || '',
  ];
  for (const c of candidates) {
    const trimmed = c.trim();
    if (trimmed && !TITLE_ONLY_RE.test(trimmed)) return trimmed;
  }
  return NOT_AVAILABLE;
}

// ─── Form field extraction ─────────────────────────────────────────────────────

/**
 * Looks up the first non-empty value among `candidates` in a dynamic-form
 * `formData` object, matching case-insensitively and ignoring `_`/`-`.
 * Used to read curated fields (code, email, phone, address) out of the
 * arbitrary per-program form schema stored on Hospital/HCP records.
 *
 * @example
 *   extractFormField(hospital.formData, ['hospitalCode', 'hospital_code', 'code'])
 */
export function extractFormField(
  formData: Record<string, unknown> | null | undefined,
  candidates: string[],
): string | null {
  if (!formData) return null;
  const normalized = new Map<string, unknown>();
  for (const [key, value] of Object.entries(formData)) {
    normalized.set(key.toLowerCase().replace(/[_-]/g, ''), value);
  }
  for (const candidate of candidates) {
    const value = normalized.get(candidate.toLowerCase().replace(/[_-]/g, ''));
    if (value != null && String(value).trim() !== '') return String(value).trim();
  }
  return null;
}

// ─── Universal Normalizers ────────────────────────────────────────────────────

const PATIENT_DTO_FALLBACK: PatientDTO = {
  id: '',
  patientCode: '—',
  fullName: NOT_AVAILABLE,
  hospitalName: NOT_AVAILABLE,
  hcpName: NOT_AVAILABLE,
  status: 'unknown',
  totalAssistance: 0,
  remainingEligibility: 0,
};

/**
 * normalizePatientDto
 *
 * Converts ANY raw patient API response into a PatientDTO.
 * Handles all known API shapes:
 *   - /patients list rows (PatientListDto)
 *   - /patients/:id detail (PatientDto with formData)
 *   - /patients/:id/360 summary (Patient360ResponseDto.patient)
 *   - workspace hierarchy rows
 *
 * Safe to call with null/undefined — returns a fully-populated fallback object.
 */
export function normalizePatientDto(raw: unknown): PatientDTO {
  if (!raw || typeof raw !== 'object') return { ...PATIENT_DTO_FALLBACK };

  const r = raw as Record<string, unknown>;
  const financials = r.financials instanceof Object ? (r.financials as Record<string, unknown>) : {};

  return {
    id: resolveId(r),
    patientCode: resolveCode(r),
    fullName: resolveFullName(r),
    firstName: r.firstName as string | undefined,
    lastName: r.lastName as string | undefined,
    hospitalName: resolveHospitalName(r),
    hcpName: resolveHcpName(r),
    status: ((r.status as string) || 'unknown').toLowerCase(),
    dob: (r.dateOfBirth ?? r.dob) as string | undefined,
    age: r.age as number | undefined,
    totalAssistance:
      (r.totalAssistance as number) ??
      (financials.totalAssistance as number) ??
      0,
    remainingEligibility:
      (r.remainingEligibility as number) ??
      (financials.remaining as number) ??
      0,
    createdAt: r.createdAt as string | undefined,
  };
}

/**
 * normalizeHcpDto
 *
 * Converts any raw HCP API response into an HcpDTO.
 * Rejects title-only names (e.g., "Dr.") and falls back to NOT_AVAILABLE.
 */
export function normalizeHcpDto(raw: unknown): HcpDTO {
  if (!raw || typeof raw !== 'object') return { id: '', fullName: NOT_AVAILABLE };
  const r = raw as Record<string, unknown>;
  return {
    id: ((r.id ?? r.hcpId ?? '') as string),
    fullName: resolveHcpName(r),
    specialization: r.specialization as string | undefined,
  };
}

/**
 * normalizeHospitalDto
 *
 * Converts any raw Hospital API response into a HospitalDTO.
 */
export function normalizeHospitalDto(raw: unknown): HospitalDTO {
  if (!raw || typeof raw !== 'object') return { id: '', name: NOT_AVAILABLE };
  const r = raw as Record<string, unknown>;
  const name = (r.hospitalName ?? r.name ?? r.facilityName) as string | undefined;
  return {
    id: ((r.id ?? r.hospitalId ?? '') as string),
    name: name?.trim() || NOT_AVAILABLE,
  };
}
